import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useToaster } from "../components/Toaster";
import styles from "./PublicChat.module.css";

const LS_PREFIX = "public_chat_session_";

function genId(prefix = "m") {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return `${prefix}_${crypto.randomUUID()}`;
  } catch {}
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

const HAS_FIELD_SIZING =
  typeof CSS !== "undefined" &&
  typeof CSS.supports === "function" &&
  CSS.supports("field-sizing", "content");

// Do not expose internal retrieval/citation markers in public chat.
// Backend removes them for new/history responses; keep this here as a defensive
// renderer for any old or cached message that still contains one.
const INTERNAL_CITATION_RE = /[ \t]*(?:(?:filecite|cite|source)[^\r\n]*|【[^】\r\n]*†[^】\r\n]*】)/g;
const INLINE_LINK_RE = /\[([^\]\n]+)\]\((https?:\/\/[A-Z0-9\-._~:/?#\[\]@!$&'*+,;=%]+)\)|(https?:\/\/[A-Z0-9\-._~:/?#\[\]@!$&'*+,;=%]+)|([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi;
const TRAILING_URL_PUNCTUATION = /[.,!?;:，。！？；：、）》】」』”’]+$/;

function cleanMessageText(value) {
  return String(value ?? "")
    .replace(INTERNAL_CITATION_RE, "")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function splitUrlTrailingPunctuation(value) {
  const match = String(value || "").match(TRAILING_URL_PUNCTUATION);
  if (!match) return [value, ""];
  return [value.slice(0, -match[0].length), match[0]];
}

function MessageContent({ content }) {
  const text = cleanMessageText(content);
  const nodes = [];
  let cursor = 0;
  let match;
  let key = 0;

  INLINE_LINK_RE.lastIndex = 0;
  while ((match = INLINE_LINK_RE.exec(text)) !== null) {
    if (match.index > cursor) {
      nodes.push(text.slice(cursor, match.index));
    }

    const markdownLabel = match[1];
    const markdownUrl = match[2];
    const plainUrl = match[3];
    const email = match[4];

    if (markdownUrl) {
      nodes.push(
        <a
          key={`link_${key++}`}
          className={styles.msgLink}
          href={markdownUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {markdownLabel}
        </a>
      );
    } else if (plainUrl) {
      const [url, trailing] = splitUrlTrailingPunctuation(plainUrl);
      nodes.push(
        <a
          key={`link_${key++}`}
          className={styles.msgLink}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {url}
        </a>
      );
      if (trailing) nodes.push(trailing);
    } else if (email) {
      nodes.push(
        <a key={`link_${key++}`} className={styles.msgLink} href={`mailto:${email}`}>
          {email}
        </a>
      );
    }

    cursor = match.index + match[0].length;
  }

  if (cursor < text.length) nodes.push(text.slice(cursor));
  return <>{nodes}</>;
}

const MessageBubble = memo(function MessageBubble({ message }) {
  const m = message;

  return (
    <div
      className={`${styles.msgRow} ${m.role === "user" ? styles.userRow : styles.assistantRow}`}
    >
      <div className={`${styles.bubble} ${m.typing ? styles.typingBubble : ""}`}>
        {m.typing ? (
          <div className={styles.typingInner}>
            <div className={styles.typingDots} aria-label="對方正在回應">
              <span />
              <span />
              <span />
            </div>
            <div className={styles.typingLabel}></div>
          </div>
        ) : (
          <>
            <div className={styles.msgText}><MessageContent content={m.content} /></div>
            {!!m.time && <div className={styles.msgMeta}>{m.time}</div>}
          </>
        )}
      </div>
    </div>
  );
});

const MessageList = memo(function MessageList({ messages, isInitialLoading, listRef }) {
  return (
    <div className={styles.chatScroll} ref={listRef}>
      {messages.length === 0 ? (
        isInitialLoading ? (
          <div className={styles.loadingChat}>
            <div className={styles.skelBubble} />
            <div className={styles.skelBubbleWide} />
            <div className={styles.skelBubble} />
          </div>
        ) : (
          <div className={styles.empty}>
            <div className={styles.emptyInner}>
              <i className="bi bi-robot fs-2" />
              <div className="mt-2">輸入一句話開始聊天</div>
            </div>
          </div>
        )
      ) : (
        messages.map((m) => <MessageBubble key={m.id} message={m} />)
      )}
    </div>
  );
});

const Composer = memo(function Composer({ disabled, sending, resetKey, onSend }) {
  const [text, setText] = useState("");
  const textareaRef = useRef(null);
  const autosizeRafRef = useRef(0);

  const canSend = useMemo(() => {
    const t = (text || "").trim();
    return !disabled && !sending && t.length > 0 && t.length <= 8000;
  }, [disabled, sending, text]);

  const focusTextarea = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.focus();
    try {
      const len = ta.value.length;
      ta.setSelectionRange(len, len);
    } catch {}
  }, []);

  // Chrome / Edge 新版直接交給 CSS field-sizing，避免每打一個字都讀 scrollHeight 強制 reflow。
  // 不支援 field-sizing 的瀏覽器才使用舊式 JS fallback，且會合併成單一 animation frame。
  useEffect(() => {
    if (HAS_FIELD_SIZING) return undefined;

    cancelAnimationFrame(autosizeRafRef.current);
    autosizeRafRef.current = requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (!ta) return;
      ta.style.height = "auto";
      ta.style.height = `${Math.min(ta.scrollHeight, 180)}px`;
    });

    return () => cancelAnimationFrame(autosizeRafRef.current);
  }, [text]);

  useEffect(() => {
    setText("");
    requestAnimationFrame(focusTextarea);
  }, [focusTextarea, resetKey]);

  const submit = useCallback(() => {
    if (!canSend) return;
    const content = (text || "").trim();
    setText("");
    onSend(content);
    requestAnimationFrame(focusTextarea);
  }, [canSend, focusTextarea, onSend, text]);

  return (
    <div className={styles.inputBar}>
      <div className={styles.composer}>
        <textarea
          ref={textareaRef}
          className={styles.textarea}
          rows={1}
          placeholder="輸入訊息"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.nativeEvent.isComposing) return;

            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          disabled={disabled}
        />

        <div className={styles.actionRow}>
          <div className={styles.hint}>Enter 送出 / Shift+Enter 換行</div>

          <button
            type="button"
            className={styles.sendBtn}
            onClick={submit}
            disabled={!canSend}
            aria-label="送出"
            title="送出"
          >
            {sending ? (
              <span className={`spinner-border spinner-border-sm ${styles.sendSpinner}`} />
            ) : (
              <i className="bi bi-arrow-up" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
});

export default function PublicChat() {
  const { assistantToken } = useParams();
  const { push } = useToaster();

  const [assistant, setAssistant] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);

  const [sessionToken, setSessionToken] = useState(() => {
    try {
      return localStorage.getItem(`${LS_PREFIX}${assistantToken}`) || "";
    } catch {
      return "";
    }
  });

  // { id, role:'user'|'assistant', content, time?, typing? }
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [composerResetKey, setComposerResetKey] = useState(0);

  const listRef = useRef(null);
  const scrollRafRef = useRef(0);
  const sendingRef = useRef(false);
  const skipHistorySessionRef = useRef("");

  const isInitialLoading = loadingInfo && !assistant;

  const queueScrollToBottom = useCallback(() => {
    cancelAnimationFrame(scrollRafRef.current);
    scrollRafRef.current = requestAnimationFrame(() => {
      const el = listRef.current;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    });
  }, []);

  useEffect(() => {
    return () => cancelAnimationFrame(scrollRafRef.current);
  }, []);

  // 1) 讀取 assistant 公開資訊
  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    setLoadingInfo(true);
    setAssistant(null);

    fetch(`/api/public/assistant_info/${assistantToken}/`, { signal: controller.signal })
      .then(async (res) => {
        const json = await res.json().catch(() => ({}));
        if (!res.ok || json?.status !== "OK") {
          throw new Error(json?.msg || "讀取代理人失敗");
        }
        return json.data;
      })
      .then((data) => {
        if (!alive) return;
        setAssistant(data);
      })
      .catch((e) => {
        if (!alive || e?.name === "AbortError") return;
        push({ title: "錯誤", message: e?.message || "讀取失敗", variant: "danger" });
      })
      .finally(() => {
        if (!alive) return;
        setLoadingInfo(false);
      });

    return () => {
      alive = false;
      controller.abort();
    };
  }, [assistantToken, push]);

  // 2) 有 sessionToken 就拉歷史
  useEffect(() => {
    let alive = true;
    const controller = new AbortController();

    if (!sessionToken) {
      setMessages([]);
      return () => controller.abort();
    }

    // 第一則訊息送出後，後端才配發 session_token。畫面上已經有這一輪訊息，
    // 不需要因 setSessionToken 再立刻抓一次完整 history、重畫整串訊息。
    if (skipHistorySessionRef.current === sessionToken) {
      skipHistorySessionRef.current = "";
      return () => controller.abort();
    }

    fetch(`/api/public/chat/${assistantToken}/history/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_token: sessionToken, limit: 100 }),
      signal: controller.signal,
    })
      .then(async (res) => {
        const json = await res.json().catch(() => ({}));
        if (!res.ok || json?.status !== "OK") {
          throw new Error(json?.msg || "讀取對話失敗");
        }
        return json.data?.messages || [];
      })
      .then((rows) => {
        if (!alive) return;
        const msgs = (rows || []).map((r) => ({
          id: r.id != null ? `h_${r.id}` : genId("h"),
          role: r.role,
          content: r.content,
          time: r.time,
        }));
        setMessages(msgs);
      })
      .catch((e) => {
        if (!alive || e?.name === "AbortError") return;
        try {
          localStorage.removeItem(`${LS_PREFIX}${assistantToken}`);
        } catch {}
        setSessionToken("");
        setMessages([]);
      });

    return () => {
      alive = false;
      controller.abort();
    };
  }, [assistantToken, sessionToken]);

  // 訊息變更時只排一個 scroll frame；不在每個 setMessages 後重複排兩層 RAF。
  useEffect(() => {
    queueScrollToBottom();
  }, [messages, queueScrollToBottom]);

  const startNewChat = useCallback(() => {
    if (sendingRef.current) return;

    try {
      localStorage.removeItem(`${LS_PREFIX}${assistantToken}`);
    } catch {}
    setSessionToken("");
    setMessages([]);
    setComposerResetKey((v) => v + 1);
    push({ title: "已重置", message: "已開始新對話", variant: "success" });
  }, [assistantToken, push]);

  const send = useCallback(async (rawContent) => {
    const content = (rawContent || "").trim();
    if (sendingRef.current || !content || content.length > 8000) return;

    sendingRef.current = true;
    setSending(true);

    const userId = genId("u");
    const typingId = genId("t");

    setMessages((prev) => [
      ...prev,
      { id: userId, role: "user", content, time: null },
      { id: typingId, role: "assistant", content: "", time: null, typing: true },
    ]);

    try {
      const res = await fetch(`/api/public/chat/${assistantToken}/message/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: content,
          session_token: sessionToken || undefined,
        }),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.status !== "OK") {
        throw new Error(json?.msg || "送出失敗");
      }

      const data = json.data || {};
      const newSession = data.session_token || "";

      if (newSession && newSession !== sessionToken) {
        skipHistorySessionRef.current = newSession;
        setSessionToken(newSession);
        try {
          localStorage.setItem(`${LS_PREFIX}${assistantToken}`, newSession);
        } catch {}
      }

      const assistantText = data.message || "";
      setMessages((prev) =>
        prev.map((m) =>
          m.id === typingId ? { ...m, typing: false, content: assistantText } : m
        )
      );
    } catch (e) {
      setMessages((prev) => prev.filter((m) => m.id !== typingId));
      push({ title: "錯誤", message: e?.message || "送出失敗", variant: "danger" });
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }, [assistantToken, push, sessionToken]);

  return (
    <div className={styles.wrap}>
      <div className={styles.head}>
        <div className={styles.titleRow}>
          <h4 className={styles.title}>
            <i className="bi bi-chat-dots me-2" />
            {isInitialLoading ? <span className={styles.skelTitle} /> : (assistant?.name || "公開聊天")}
          </h4>
        </div>

        {isInitialLoading ? (
          <div className={styles.skelDescWrap}>
            <div className={styles.skelLine} />
            <div className={styles.skelLineShort} />
          </div>
        ) : (
          <>
            {assistant?.description && <div className={styles.desc}>{assistant.description}</div>}
            <div className={styles.headActions}>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={startNewChat}
                disabled={isInitialLoading || sending}
              >
                <i className="bi bi-arrow-counterclockwise me-1" />
                新對話
              </button>
            </div>
          </>
        )}
      </div>

      <div className={styles.chatBox}>
        <MessageList
          messages={messages}
          isInitialLoading={isInitialLoading}
          listRef={listRef}
        />
      </div>

      <Composer
        disabled={isInitialLoading}
        sending={sending}
        resetKey={composerResetKey}
        onSend={send}
      />
    </div>
  );
}
